import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import AudioFileUpload from './AudioFileUpload';
import BatchAnalysis from './BatchAnalysis';
import WavToTxtConverter from './WavToTxtConverter';
import AnalysisResults from './AnalysisResults';
import audioBuzzAPI from '../../services/audioBuzzApi';

function AudioAnalysis() {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [activeTab, setActiveTab] = useState('single'); // 'single', 'batch', or 'convert'
  const [apiStatus, setApiStatus] = useState('checking');
  const [errorMessage, setErrorMessage] = useState('');

  // Check API health on component mount
  React.useEffect(() => {
    const checkApiHealth = async () => {
      try {
        await audioBuzzAPI.healthCheck();
        setApiStatus('connected');
      } catch (error) {
        setApiStatus('disconnected');
        setErrorMessage('Cannot connect to AudioBuzz API. Make sure the backend is running.');
      }
    };
    checkApiHealth();
  }, []);

  const handleSingleAnalysisComplete = useCallback((result) => {
    setResults([result]);
    setErrorMessage('');
  }, []);

  const handleBatchAnalysisComplete = useCallback((batchResults) => {
    setResults(batchResults);
    setErrorMessage('');
  }, []);

  const handleError = useCallback((error) => {
    setErrorMessage(error);
    console.error('Analysis error:', error);
  }, []);

  const handleConversionComplete = useCallback((conversionResults) => {
    console.log('Conversion completed:', conversionResults);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="flex items-center p-4 border-b border-gray-800">
        <button
          onClick={() => navigate('/dashboard')}
          className="mr-4 p-2 hover:bg-gray-800 rounded-lg transition-colors"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-medium">Audio Issue Visualization</h1>
          <p className="text-sm text-gray-400">Advanced audio spectrum analysis and buzz detection</p>
        </div>
      </div>

      {/* API Status Indicator */}
      <div className="px-4 py-4 border-b border-gray-800">
        <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
          apiStatus === 'checking' ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-800' :
          apiStatus === 'connected' ? 'bg-green-900/30 text-green-400 border border-green-800' :
          'bg-red-900/30 text-red-400 border border-red-800'
        }`}>
          {apiStatus === 'checking' && '🔄 Checking API connection...'}
          {apiStatus === 'connected' && '✅ API Connected'}
          {apiStatus === 'disconnected' && '❌ API Disconnected'}
        </div>
      </div>

      {/* Navigation Tabs */}
      <nav className="px-4 py-4 border-b border-gray-800">
        <div className="flex flex-wrap gap-2">
          <button 
            className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
              activeTab === 'single' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
            } ${apiStatus !== 'connected' ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => setActiveTab('single')}
            disabled={apiStatus !== 'connected'}
          >
            🎵 Single File Analysis
          </button>
          <button 
            className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
              activeTab === 'batch' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
            } ${apiStatus !== 'connected' ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => setActiveTab('batch')}
            disabled={apiStatus !== 'connected'}
          >
            📁 Batch Analysis
          </button>
          <button 
            className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
              activeTab === 'convert' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
            } ${apiStatus !== 'connected' ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => setActiveTab('convert')}
            disabled={apiStatus !== 'connected'}
          >
            🔁 WAV → TXT Converter
          </button>
        </div>
      </nav>

      {/* Error Message */}
      {errorMessage && (
        <div className="px-4 py-4">
          <div className="bg-red-900/30 border border-red-800 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <span className="text-red-400 text-xl">⚠️</span>
              </div>
              <div className="ml-3">
                <p className="text-red-300 font-medium">{errorMessage}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="px-4 py-8 pb-20">
        {activeTab === 'single' && apiStatus === 'connected' && (
          <div className="bg-gray-900 rounded-lg p-6 mb-8 border border-gray-800">
            <AudioFileUpload 
              onAnalysisComplete={handleSingleAnalysisComplete}
              onError={handleError}
            />
          </div>
        )}

        {activeTab === 'batch' && apiStatus === 'connected' && (
          <div className="bg-gray-900 rounded-lg p-6 mb-8 border border-gray-800">
            <BatchAnalysis 
              onBatchComplete={handleBatchAnalysisComplete}
              onError={handleError}
            />
          </div>
        )}

        {activeTab === 'convert' && apiStatus === 'connected' && (
          <div className="bg-gray-900 rounded-lg p-6 mb-8 border border-gray-800">
            <WavToTxtConverter 
              onConversionComplete={handleConversionComplete}
              onError={handleError}
            />
          </div>
        )}

        {apiStatus === 'disconnected' && (
          <div className="bg-gray-900 rounded-lg p-8 text-center border border-gray-800">
            <div className="mb-6">
              <span className="text-6xl">🚫</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">API Connection Required</h2>
            <p className="text-gray-400 mb-6">The AudioBuzz API backend is not running or not accessible.</p>
            <div className="bg-gray-800 rounded-lg p-6 text-left max-w-2xl mx-auto border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4">To start the API backend:</h3>
              <ol className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <span className="bg-blue-900/50 text-blue-300 text-sm font-medium px-2 py-1 rounded mr-3 mt-0.5 border border-blue-700">1</span>
                  Open terminal in the project directory
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-900/50 text-blue-300 text-sm font-medium px-2 py-1 rounded mr-3 mt-0.5 border border-blue-700">2</span>
                  Navigate to the api folder: <code className="bg-gray-700 px-2 py-1 rounded text-sm ml-2">cd api</code>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-900/50 text-blue-300 text-sm font-medium px-2 py-1 rounded mr-3 mt-0.5 border border-blue-700">3</span>
                  Run: <code className="bg-gray-700 px-2 py-1 rounded text-sm ml-2">docker-compose up --build</code>
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-900/50 text-blue-300 text-sm font-medium px-2 py-1 rounded mr-3 mt-0.5 border border-blue-700">4</span>
                  Wait for the API to start
                </li>
                <li className="flex items-start">
                  <span className="bg-blue-900/50 text-blue-300 text-sm font-medium px-2 py-1 rounded mr-3 mt-0.5 border border-blue-700">5</span>
                  Refresh this page
                </li>
              </ol>
            </div>
          </div>
        )}

        <AnalysisResults 
          results={results} 
          parameters={{ exportExcel: true, thresholdStrong: -45 }} 
        />
      </main>
    </div>
  );
}

export default AudioAnalysis;
